import moment from "moment";
import "moment/locale/zh-hk";
import type { ActivityInfo } from "@/services/api/activity/types";

export const withMomentLocale = (lang: string) => {
  moment.locale(lang === "zh_hant" ? "zh-hk" : "en");
};

export const localized = (
  zh: string | null | undefined,
  en: string | null | undefined,
  lang: string,
): string => {
  if (lang === "en") {
    return en && en.trim().length > 0 ? en : zh ?? "";
  }
  return zh && zh.trim().length > 0 ? zh : en ?? "";
};

export const toImageList = (images: unknown): string[] => {
  if (!images) return [];
  if (Array.isArray(images)) {
    return images.filter((item): item is string => typeof item === "string");
  }
  if (typeof images === "string") {
    try {
      const parsed = JSON.parse(images);
      if (Array.isArray(parsed)) {
        return parsed.filter((item): item is string => typeof item === "string");
      }
    } catch {
      return [images];
    }
  }
  return [];
};

export const formatDate = (date?: string | null): string => {
  if (!date) return "";
  const m = moment(date);
  if (!m.isValid()) return date;
  return m.format("YYYY-MM-DD");
};

export const formatTime = (time?: string | null): string => {
  if (!time) return "";
  return time;
};

/**
 * Normalize a time into the `hh:mm a` clock the activity API stores. Form
 * inputs hand over 24-hour `HH:mm`; values already in 12-hour form pass
 * through unchanged when moment cannot parse them.
 */
export const toTwelveHourClock = (time?: string | null): string => {
  if (!time) return "";
  const parsed = moment(time, ["HH:mm", "H:mm", "hh:mm a", "h:mm a"], true);
  return parsed.isValid() ? parsed.format("hh:mm a") : time;
};

/**
 * Normalize a stored `hh:mm a` time into the 24-hour `HH:mm` an `<input
 * type="time">` expects. Values already in 24-hour form pass through unchanged.
 */
export const toTwentyFourHourClock = (time?: string | null): string => {
  if (!time) return "";
  const parsed = moment(time, ["hh:mm a", "h:mm a", "HH:mm", "H:mm"], true);
  return parsed.isValid() ? parsed.format("HH:mm") : time;
};

export const formatDateTime = (date?: string | null, time?: string | null): string => {
  const d = formatDate(date);
  const t = formatTime(time);
  if (d && t) return `${d} ${t}`;
  return d || t || "";
};
